import { apiFetch } from './api';

const apiUrl = import.meta.env.VITE_API_URL;

/**
 * Sistema › Assistente (só GB Code): o que perguntam ao assistente da Home, em
 * todas as empresas, como ele respondeu, o que consultou e quanto custou.
 *
 * docs/designsystem/propostas/admin/fase-3-assistente.html (Direção B).
 */

/**
 * Como a resposta terminou. Os seis primeiros são gravados pela API desde
 * 26/09/2026; `nao_medido` é a resposta anterior a isso, e `sem_resposta` é a
 * pergunta cuja resposta nunca foi gravada.
 */
export type Desfecho =
    | 'respondida' | 'sem_dado' | 'fora_do_assunto' | 'conversa' | 'sem_consulta' | 'falha'
    | 'nao_medido' | 'sem_resposta';

export interface ResumoAssistente {
    medindoDesde: string | null;
    perguntas: number;
    empresas: number;
    pessoas: number;
    deSugestao: number;
    medidas: number;
    respondidas: number;
    taxaResposta: number | null;
    semResposta: number;
    porDesfecho: { desfecho: Desfecho; total: number }[];
    uteis: number;
    naoUteis: number;
    tempoMedianaMs: number | null;
    tempoP90Ms: number | null;
    tokensEntrada: number;
    tokensSaida: number;
    custoEstimado: number;
    custoPorPergunta: number | null;
    tarifaVigenteDesde: string | null;
    cotacao: number | null;
    ferramentas: { ferramenta: string; rotulo: string; usos: number; falhas: number }[];
    empresasComPergunta: { empresaId: number; empresaNome: string; perguntas: number }[];
}

export interface PerguntaLinha {
    mensagemId: number;
    conversaId: number;
    pergunta: string;
    empresaId: number;
    empresaNome: string;
    usuarioNome: string;
    origem: 'digitada' | 'sugestao' | null;
    conversaApagada: boolean;
    criadaEm: string;
    respostaId: number | null;
    desfecho: Desfecho;
    desfechoDetalhe: string | null;
    consultou: string[];
    duracaoMs: number | null;
    avaliacao: 1 | -1 | null;
}

export interface PaginaPerguntas {
    total: number;
    pagina: number;
    tamanho: number;
    itens: PerguntaLinha[];
}

export interface RastroFerramenta {
    ferramenta: string;
    rotulo: string;
    argumentos: Record<string, unknown>;
    ok: boolean;
    erro: string | null;
    ms: number;
}

export interface MensagemDaConversa {
    mensagemId: number;
    papel: 'user' | 'model';
    conteudo: string;
    criadaEm: string;
    origem: 'digitada' | 'sugestao' | null;
    desfecho: Desfecho | null;
    desfechoDetalhe: string | null;
    rastro: RastroFerramenta[];
    fontes: string[];
    rodadas: number | null;
    tokensEntrada: number | null;
    tokensSaida: number | null;
    modelo: string | null;
    duracaoMs: number | null;
    custoEstimado: number | null;
    avaliacao: 1 | -1 | null;
}

export interface ConversaDaPergunta {
    conversaId: number;
    mensagemSelecionadaId: number;
    empresaNome: string;
    usuarioNome: string;
    conversaApagada: boolean;
    mensagens: MensagemDaConversa[];
}

export interface FiltroPerguntas {
    de: string;
    ate: string;
    desfecho?: string | null;
    empresaId?: number | null;
    ferramenta?: string | null;
    avaliacao?: 'util' | 'nao_util' | 'sem' | null;
    busca?: string;
    pagina?: number;
    tamanho?: number;
}

const ler = async <T,>(res: Response): Promise<T> => {
    const json = await res.json().catch(() => null);
    if (!res.ok || !json?.sucesso) throw new Error(json?.mensagem || 'Não foi possível carregar.');
    return json.dados as T;
};

export const AssistenteSistemaService = {
    resumo: async (de: string, ate: string): Promise<ResumoAssistente> =>
        ler(await apiFetch(`${apiUrl}/api/sistema/assistente/resumo?de=${de}&ate=${ate}`)),

    perguntas: async (f: FiltroPerguntas): Promise<PaginaPerguntas> => {
        const q = new URLSearchParams({ de: f.de, ate: f.ate });
        if (f.desfecho) q.set('desfecho', f.desfecho);
        if (f.empresaId) q.set('empresaId', String(f.empresaId));
        if (f.ferramenta) q.set('ferramenta', f.ferramenta);
        if (f.avaliacao) q.set('avaliacao', f.avaliacao);
        if (f.busca?.trim()) q.set('busca', f.busca.trim());
        q.set('pagina', String(f.pagina ?? 1));
        q.set('tamanho', String(f.tamanho ?? 50));
        return ler(await apiFetch(`${apiUrl}/api/sistema/assistente/perguntas?${q}`));
    },

    conversa: async (mensagemId: number): Promise<ConversaDaPergunta> =>
        ler(await apiFetch(`${apiUrl}/api/sistema/assistente/perguntas/${mensagemId}`)),
};
